import { useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Dialog, useDialogRequestClose } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { Button } from "@/components/ui/Button";
import { SortableColumnPicker } from "@/components/dataTable";
import {
  PROJECT_REQUIRED_COLUMNS,
  getAvailableColumns,
  getColumnLabel,
  getDefaultColumns,
  normalizeColumnSelection,
} from "@/pages/projekte/columns";
import type { HeroCustomField } from "@/pages/projekte/columns";
import type { ProjectColumnSettings, ProjectColumnView } from "@/lib/projects";

export interface ProjectColumnsDialogProps {
  open: boolean;
  onClose: () => void;
  heroFields: HeroCustomField[];
  /** Aktueller Settings-Stand (z. B. aus dem Server). */
  settings: ProjectColumnSettings;
  /** Wird mit den neuen Settings aufgerufen, sobald gespeichert wird. */
  onSave: (next: ProjectColumnSettings) => Promise<void> | void;
  /** Spalte „Ordner öffnen“ nur für Büro in Electron (klassische Logik). */
  showOpenFolderColumn?: boolean;
}

type View = "desktop" | "mobile";

export function ProjectColumnsDialog({
  open,
  onClose,
  heroFields,
  settings,
  onSave,
  showOpenFolderColumn = true,
}: ProjectColumnsDialogProps) {
  const available = useMemo(() => {
    const all = getAvailableColumns(heroFields);
    if (showOpenFolderColumn) return all;
    return all.filter((c) => c !== "open");
  }, [heroFields, showOpenFolderColumn]);
  const defaults = useMemo(() => {
    const d = getDefaultColumns(heroFields);
    if (showOpenFolderColumn) return d;
    return d.filter((c) => c !== "open");
  }, [heroFields, showOpenFolderColumn]);
  const [tab, setTab] = useState<View>("desktop");
  const [desktop, setDesktop] = useState<string[]>(() =>
    normalizeColumnSelection(settings.desktop?.columns, available),
  );
  const [mobile, setMobile] = useState<string[]>(() =>
    normalizeColumnSelection(settings.mobile?.columns, available),
  );
  const [saving, setSaving] = useState(false);
  const [baselineDesktop, setBaselineDesktop] = useState<string[]>(desktop);
  const [baselineMobile, setBaselineMobile] = useState<string[]>(mobile);

  useEffect(() => {
    if (!open) return;
    const nextDesktop =
      settings.desktop?.columns?.length
        ? normalizeColumnSelection(settings.desktop.columns, available)
        : defaults;
    const nextMobile =
      settings.mobile?.columns?.length
        ? normalizeColumnSelection(settings.mobile.columns, available)
        : defaults;
    setDesktop(nextDesktop);
    setMobile(nextMobile);
    setBaselineDesktop(nextDesktop);
    setBaselineMobile(nextMobile);
  }, [open, settings, available, defaults]);

  const current = tab === "desktop" ? desktop : mobile;
  const setCurrent = tab === "desktop" ? setDesktop : setMobile;

  function selectAll() {
    setCurrent(available);
  }

  function restoreDefaults() {
    setCurrent(defaults);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const desktopView: ProjectColumnView = {
        columns: normalizeColumnSelection(desktop, available),
        widths: settings.desktop?.widths ?? {},
      };
      const mobileView: ProjectColumnView = {
        columns: normalizeColumnSelection(mobile, available),
        widths: settings.mobile?.widths ?? {},
      };
      await onSave({ desktop: desktopView, mobile: mobileView });
      onClose();
    } finally {
      setSaving(false);
    }
  }

  const dirty = useMemo(
    () =>
      hasFormChanges(desktop, baselineDesktop) || hasFormChanges(mobile, baselineMobile),
    [desktop, baselineDesktop, mobile, baselineMobile],
  );

  return (
    <ProjectColumnsDialogView
      open={open}
      onClose={onClose}
      dirty={dirty}
      heroFields={heroFields}
      tab={tab}
      setTab={setTab}
      current={current}
      available={available}
      onSelectedChange={setCurrent}
      selectAll={selectAll}
      restoreDefaults={restoreDefaults}
      saving={saving}
      onSave={() => void handleSave()}
    />
  );
}

function ProjectColumnsDialogView({
  open,
  onClose,
  dirty,
  heroFields,
  tab,
  setTab,
  current,
  available,
  onSelectedChange,
  selectAll,
  restoreDefaults,
  saving,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  dirty: boolean;
  heroFields: HeroCustomField[];
  tab: View;
  setTab: (v: View) => void;
  current: string[];
  available: string[];
  onSelectedChange: (next: string[]) => void;
  selectAll: () => void;
  restoreDefaults: () => void;
  saving: boolean;
  onSave: () => void;
}) {
  const requestClose = useDialogRequestClose(onClose);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={dirty}
      title="Spalten anpassen"
      description="Wähle die Spalten für die Tabelle und ziehe sie per Drag-and-Drop in die gewünschte Reihenfolge."
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={restoreDefaults} disabled={saving}>
            Standard wiederherstellen
          </Button>
          <Button variant="secondary" onClick={selectAll} disabled={saving}>
            Alle auswählen
          </Button>
          <Button variant="ghost" onClick={requestClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={onSave} loading={saving}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="mb-3 flex gap-1 rounded-full bg-fill-tertiary p-1 text-sm">
        <TabButton active={tab === "desktop"} onClick={() => setTab("desktop")}>
          Desktop
        </TabButton>
        <TabButton active={tab === "mobile"} onClick={() => setTab("mobile")}>
          Mobil
        </TabButton>
      </div>

      <SortableColumnPicker
        available={available}
        selected={current}
        requiredColumns={PROJECT_REQUIRED_COLUMNS}
        getLabel={(col) => getColumnLabel(col, heroFields)}
        onSelectedChange={onSelectedChange}
        disabled={saving}
      />
    </Dialog>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "flex-1 rounded-full px-3 py-1.5 font-medium transition",
        active ? "bg-card text-label shadow-sm" : "text-label-secondary hover:text-label",
      )}
    >
      {children}
    </button>
  );
}
